import { Box, Container, Grid } from '@mui/material';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import { MarbleButton } from 'src/base';
import withMarketingLayout from 'src/components/layout/MarketingLayout';
import { useRouter } from 'next/router';
import { MarbleTextInput } from 'src/base/inputs/MarbleTextInput';
import { MarbleText } from 'src/base/texts/Text';
import { useApiStore } from 'ui-sdk/src/hooks';

const containerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  paddingTop: 40,
  maxWidth: 1000,
  marginBottom: 80,
};

declare let LobAddressElements: any;

// rent estimate page
function RentEstimate(): JSX.Element {
  useEffect(() => {
    const script = document.createElement('script');
    script.src =
      'https://cdn.lob.com/lob/address-elements/2.2.1/address-elements.min.js';
    script.async = true;
    script.setAttribute(
      'data-lob-key',
      'live_pub_b7b53c293be0d658fafcdfc148e3ffa',
    );
    script.setAttribute('data-lob-primary-id', 'inputAddress1');
    script.setAttribute('data-lob-city-id', 'inputCity');
    script.setAttribute('data-lob-state-id', 'inputState');
    script.setAttribute('data-lob-zip-id', 'inputZip');
    document.body.appendChild(script);

    script.addEventListener('load', () => {
      if (typeof LobAddressElements !== 'undefined') {
        LobAddressElements.on(
          'elements.us_autocompletion.selection',
          (payload, event) => {
            console.log(event.topic, payload);
            const addr1 = payload.selection.primary_line;
            const { city } = payload.selection;
            const { state } = payload.selection;
            const zip = payload.selection.zip_code;
            setAddr1(addr1);
            setCity(city);
            setState(state);
            setZip(zip);
          },
        );
      }
    });
  }, [typeof LobAddressElements]);

  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');

  const [loading, setLoading] = useState(false);

  const [, submitLookup] = useApiStore((a) => a.rentEstimate.request);

  const router = useRouter();

  const esimatePressed = async () => {
    setLoading(true);
    const qp: any = {};
    if (beds) {
      qp.beds = beds;
    }
    if (baths) {
      qp.baths = baths;
    }
    submitLookup(
      {
        address: {
          addr1,
          addr2,
          city,
          state,
          zip,
        },
        bedrooms: parseInt(beds),
        bathrooms: parseFloat(baths),
      },
      (a) => {
        router.push(
          {
            pathname: `/rent-estimate/${a.addr_slug}`,
          },
          undefined,
          {
            shallow: true,
          },
        );
      },
    );
  };

  return (
    <>
      <Head>
        <title>Free Rent Estimate - Marble Virtual Rental Management</title>
        <meta
          name="description"
          content="Get a free rent estimate for your rental property!"
        />
      </Head>
      <Container style={containerStyle}>
        <MarbleText size="h2" weight="bold">
          Free Rent Estimate
        </MarbleText>
        <MarbleText
          weight="med"
          sx={{
            pt: 1,
          }}
        >
          Analyzing millions of rental housing data points across the US in
          realtime.
        </MarbleText>
        <Grid
          container
          sx={{
            marginTop: 10,
          }}
          direction="row"
        >
          <Grid item sm={7} p={1}>
            <Box
              sx={(t) => ({
                borderRadius: 1,
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                pt: 9,
                pb: 9,
                pl: 10,
                pr: 10,
                [t.breakpoints.down('sm')]: {
                  p: 4,
                },
              })}
            >
              <form>
                <MarbleTextInput
                  inputId="inputAddress1"
                  placeholder="Street Address"
                  value={addr1}
                  // @ts-ignore
                  onChange={(event) => setAddr1(event.target.value)}
                  required
                />
                <div style={{ marginTop: 10, display: 'none' }}>
                  <MarbleTextInput
                    inputId="inputAddress2"
                    placeholder="Unit Number (optional)"
                    value={addr2}
                    // @ts-ignore
                    onChange={(e) => setAddr2(e.target.value)}
                  />
                </div>
                <Box sx={{ marginTop: 5 }}>
                  <MarbleTextInput
                    inputId="inputCity"
                    placeholder="City"
                    value={city}
                    // @ts-ignore
                    onChange={(e) => setCity(e.target.value)}
                    required
                  />
                </Box>
                <Box
                  sx={{ marginTop: 5, display: 'flex', flexDirection: 'row' }}
                >
                  <Box sx={{ flex: 2, pr: 1 }}>
                    <MarbleTextInput
                      inputId="inputState"
                      placeholder="State"
                      value={state}
                      // @ts-ignore
                      onChange={(e) => setState(e.target.value)}
                      required
                    />
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <MarbleTextInput
                      inputId="inputZip"
                      placeholder="Zip Code"
                      value={zip}
                      // @ts-ignore
                      onChange={(e) => setZip(e.target.value)}
                      required
                    />
                  </Box>
                </Box>
                <Box
                  sx={{ marginTop: 5, display: 'flex', flexDirection: 'row' }}
                >
                  <Box sx={{ flex: 2, pr: 1 }}>
                    <MarbleTextInput
                      inputId="inputBeds"
                      placeholder="Beds"
                      value={beds}
                      // @ts-ignore
                      onChange={(e) => setBeds(e.target.value)}
                      required
                    />
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <MarbleTextInput
                      inputId="intpuBaths"
                      placeholder="Baths"
                      value={baths}
                      // @ts-ignore
                      onChange={(e) => setBaths(e.target.value)}
                      required
                    />
                  </Box>
                </Box>
              </form>
              <MarbleButton
                title="Calculate"
                onClick={esimatePressed}
                loading={loading}
                sx={{
                  mt: 5,
                }}
              />
            </Box>
          </Grid>
          <Grid item sm={5} p={0}>
            <Box
              sx={(t) => ({
                p: 1,
                pl: 16,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                [t.breakpoints.down('sm')]: {
                  p: 1,
                  pt: 12,
                },
              })}
            >
              <MarbleText size="xl" weight="bold">
                How It Works
              </MarbleText>
              <MarbleText
                sx={{
                  pt: 2,
                }}
              >
                Our system looks at the rental housing supply across the US in
                realtime to constantly re-evaluate the data model that supports
                our rent estimates. This provides the most up-to-date
                information on pricing, using current properties in the area.
              </MarbleText>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default withMarketingLayout(RentEstimate);
